import React, { useEffect, useMemo, useRef, useState } from 'react';
import Parse from '../../parseConfig';
import { BUILT_HOME_TYPES, BUILD_STYLES, cleanImportWarnings, formatCoordinates, formatDateInput, getCoordinateSaveAction, normalizeAddress, parseCoordinates, parseDateInput } from './builtProjectUtils';
import { fetchSalesConsultants } from './fetchSalesConsultants';
import { sanitizeImageFile } from './imageSanitizer';
import BuiltCoordinatePicker, { MapPinIcon } from './BuiltCoordinatePicker';
import BuiltAdminDropdown from './BuiltAdminDropdown';
import BuiltFileDropzone from './BuiltFileDropzone';
import { createBuiltHomeAcl, getBuiltHomeAccessRoles } from './builtHomeAccess';
import useBuiltModalEscape from './useBuiltModalEscape';
import {
  CUSTOM_BASE_PLAN_VALUE,
  getBasePlanDraftUpdate,
  getBasePlanOptions,
  getBasePlanSelectionValue,
  saveBasePlanSelection,
} from './basePlanSelection';
import TrashIcon from '../../images/trash.svg';

const BuiltHome = Parse.Object.extend('BuiltHome');
const Plan = Parse.Object.extend('Plan');
const MANUAL_CONSULTANT_VALUE = '__manual__';

const ReviewChevron = ({ direction }) => (
  <svg className="built-review-chevron" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
    <path d={direction === 'left' ? 'M12.5 4.5 7 10l5.5 5.5' : 'M7.5 4.5 13 10l-5.5 5.5'} />
  </svg>
);

const UploadIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 20 20" focusable="false">
    <path d="M10 3.5 5.8 7.7l1.1 1.1 2.3-2.3v7h1.6v-7l2.3 2.3 1.1-1.1L10 3.5Z" />
    <path d="M4.5 15h11v1.5h-11V15Z" />
  </svg>
);

const createReviewDraft = (home) => ({
  homeId: home?.id || '',
  address: home?.address || '',
  coordinates: formatCoordinates(home?.coordinates?.lat, home?.coordinates?.lng),
  coordinatesEdited: false,
  clientName: home?.clientName || '',
  consentedDate: formatDateInput(home?.consentedDate),
  buildStyle: home?.buildStyle || '',
  type: home?.type || '',
  completedYear: home?.completedYear ?? '',
  basePlanId: home?.basePlanId || '',
  basePlanIsCustom: home?.basePlanIsCustom === true,
  salesConsultantId: home?.salesConsultantId || '',
  salesConsultantManualName: home?.salesConsultantManualName || '',
  isManualSalesConsultant: !home?.salesConsultantId && Boolean(home?.salesConsultantManualName),
  reviewed: home?.needsReview !== 1,
  photoName: home?.photoName || '',
  photoUploadFile: null,
  removePhoto: false,
  plansPdfName: home?.plansPdfName || '',
  plansPdfUploadFile: null,
  removePlansPdf: false,
});

const geocodeAddress = async (address) => {
  if (!window.google?.maps?.Geocoder) throw new Error('The map is still loading.');
  const response = await new window.google.maps.Geocoder().geocode({ address });
  const location = response.results?.[0]?.geometry?.location;
  if (!location) throw new Error('The address could not be located.');
  return { latitude: location.lat(), longitude: location.lng() };
};

const setOptionalString = (object, field, value) => {
  const trimmed = String(value || '').trim();
  if (trimmed) object.set(field, trimmed);
  else object.unset(field);
};

export default function BuiltHomeReviewModal({
  isOpen,
  homes = [],
  mode = 'review',
  home = null,
  basePlans = [],
  isLoadingBasePlans = false,
  basePlanLoadError = '',
  onClose = () => {},
  onHomesChanged = async () => {},
}) {
  const isReviewMode = mode === 'review';
  const queue = useMemo(
    () => homes
      .filter((home) => home.needsReview === 1)
      .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0)),
    [homes]
  );
  const [currentId, setCurrentId] = useState('');
  const [draft, setDraft] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSanitizingPhoto, setIsSanitizingPhoto] = useState(false);
  const [message, setMessage] = useState('');
  const [salesConsultants, setSalesConsultants] = useState([]);
  const [isCoordinatePickerOpen, setIsCoordinatePickerOpen] = useState(false);
  const [plansPdfPreviewUrl, setPlansPdfPreviewUrl] = useState('');
  const touchStartX = useRef(null);

  const currentIndex = Math.max(0, queue.findIndex((queueHome) => queueHome.id === currentId));
  const currentHome = isReviewMode
    ? queue[currentIndex] || null
    : homes.find((listedHome) => listedHome.id === home?.id) || home || null;
  const isDraftReady = Boolean(currentHome && draft?.homeId === currentHome.id);

  useBuiltModalEscape(isOpen, onClose);

  useEffect(() => {
    if (!isOpen) {
      setCurrentId('');
      setDraft(null);
      setMessage('');
      setIsSanitizingPhoto(false);
      setIsCoordinatePickerOpen(false);
      setPlansPdfPreviewUrl('');
      return;
    }
    if (isReviewMode) {
      if (!queue.some((queueHome) => queueHome.id === currentId)) setCurrentId(queue[0]?.id || '');
    } else if (home?.id !== currentId) {
      setCurrentId(home?.id || '');
    }
  }, [currentId, home?.id, isOpen, isReviewMode, queue]);

  useEffect(() => {
    if (!isOpen || !currentHome) {
      setDraft(null);
      return;
    }

    setDraft(createReviewDraft(currentHome));
    setMessage('');
  }, [currentHome, isOpen]);

  useEffect(() => {
    const file = draft?.plansPdfUploadFile;
    if (!file) {
      setPlansPdfPreviewUrl('');
      return undefined;
    }

    const objectUrl = URL.createObjectURL(file);
    setPlansPdfPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [draft?.plansPdfUploadFile]);

  useEffect(() => {
    if (!isOpen) return undefined;
    fetchSalesConsultants().then(setSalesConsultants).catch(() => setSalesConsultants([]));
    return undefined;
  }, [isOpen]);

  const move = (direction) => {
    if (!isReviewMode || !queue.length) return;
    const nextIndex = Math.min(queue.length - 1, Math.max(0, currentIndex + direction));
    setCurrentId(queue[nextIndex].id);
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (['INPUT', 'SELECT', 'TEXTAREA'].includes(event.target?.tagName)) return;
      if (isReviewMode && event.key === 'ArrowLeft') move(-1);
      if (isReviewMode && event.key === 'ArrowRight') move(1);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  if (!isOpen) return null;

  const updateDraft = (field, value) => setDraft((current) => ({ ...current, [field]: value }));

  const saveCurrent = async ({ closeAfterSave = false } = {}) => {
    if (!currentHome || !isDraftReady || isSaving || isSanitizingPhoto) return false;

    let manualCoordinates;
    let consentedDate;
    try {
      manualCoordinates = parseCoordinates(draft.coordinates);
      consentedDate = parseDateInput(draft.consentedDate);
    } catch (error) {
      setMessage(error.message);
      return false;
    }

    const completedYear = draft.completedYear === '' ? null : Number(draft.completedYear);
    const currentYear = new Date().getFullYear();
    if (completedYear !== null && (!Number.isInteger(completedYear) || completedYear < 1800 || completedYear > currentYear)) {
      setMessage(`Year built must be between 1800 and ${currentYear}.`);
      return false;
    }

    setIsSaving(true);
    setMessage('');
    let geocodeWarning = '';

    try {
      const query = new Parse.Query(BuiltHome);
      const homeObject = await query.get(currentHome.id);
      homeObject.setACL(createBuiltHomeAcl(await getBuiltHomeAccessRoles()));
      const address = draft.address.trim();

      setOptionalString(homeObject, 'address', address);
      setOptionalString(homeObject, 'addressKey', normalizeAddress(address));
      setOptionalString(homeObject, 'clientName', draft.clientName);
      setOptionalString(homeObject, 'buildStyle', draft.buildStyle);
      setOptionalString(homeObject, 'type', draft.type);
      saveBasePlanSelection(homeObject, draft, (planId) => Plan.createWithoutData(planId));

      if (draft.salesConsultantId) {
        homeObject.set('salesConsultant', Parse.User.createWithoutData(draft.salesConsultantId));
        homeObject.unset('salesConsultantName');
      } else {
        homeObject.unset('salesConsultant');
        setOptionalString(homeObject, 'salesConsultantName', draft.salesConsultantManualName);
      }

      if (completedYear === null) homeObject.unset('completedYear');
      else homeObject.set('completedYear', completedYear);

      if (consentedDate) homeObject.set('consentedDate', consentedDate);
      else homeObject.unset('consentedDate');

      const previousAddress = currentHome.address || '';
      const previousCoordinates = currentHome._raw?.get?.('geocodedCoordinates') || currentHome._raw?.get?.('coordinates');
      const coordinateSaveAction = getCoordinateSaveAction({
        previousAddress,
        nextAddress: address,
        coordinatesEdited: draft.coordinatesEdited,
        manualCoordinates,
        hasPreviousCoordinates: Boolean(previousCoordinates),
      });

      if (coordinateSaveAction === 'manual') {
        homeObject.set('geocodedCoordinates', new Parse.GeoPoint(manualCoordinates));
      } else if (coordinateSaveAction === 'geocode') {
        try {
          homeObject.set('geocodedCoordinates', new Parse.GeoPoint(await geocodeAddress(address)));
        } catch (error) {
          homeObject.unset('geocodedCoordinates');
          geocodeWarning = 'Address saved, but it could not be geocoded.';
        }
      } else if (coordinateSaveAction === 'preserve') {
        homeObject.set('geocodedCoordinates', previousCoordinates);
      } else {
        homeObject.unset('geocodedCoordinates');
      }

      homeObject.set('needsReview', draft.reviewed ? 0 : 1);
      if (draft.reviewed) {
        homeObject.unset('importWarnings');
      } else {
        const cleanedWarnings = cleanImportWarnings(currentHome._raw?.get?.('importWarnings'));
        if (cleanedWarnings.length) homeObject.set('importWarnings', cleanedWarnings);
        else homeObject.unset('importWarnings');
      }

      if (draft.photoUploadFile) {
        homeObject.set('photo', new Parse.File(draft.photoUploadFile.name, draft.photoUploadFile));
      } else if (draft.removePhoto) {
        homeObject.unset('photo');
      }

      if (draft.plansPdfUploadFile) {
        homeObject.set('plansPdf', new Parse.File(draft.plansPdfUploadFile.name, draft.plansPdfUploadFile));
      } else if (draft.removePlansPdf) {
        homeObject.unset('plansPdf');
      }

      await homeObject.save();

      const nextId = draft.reviewed && isReviewMode
        ? queue[Math.min(currentIndex + 1, queue.length - 1)]?.id || ''
        : currentHome.id;
      await onHomesChanged();
      if (draft.reviewed && isReviewMode) setCurrentId(nextId === currentHome.id ? '' : nextId);
      if (closeAfterSave) onClose();
      else setMessage(geocodeWarning || 'Saved.');
      return true;
    } catch (error) {
      console.error('Failed to save reviewed home:', error);
      setMessage(error?.message || 'The home could not be saved.');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const preparePhotoFile = async (file) => {
    if (!file || isSanitizingPhoto) return;
    setIsSanitizingPhoto(true);
    setMessage('');
    try {
      const sanitizedFile = await sanitizeImageFile(file);
      setDraft((current) => ({
        ...current,
        photoName: sanitizedFile.name,
        photoUploadFile: sanitizedFile,
        removePhoto: false,
      }));
    } catch (error) {
      setMessage(error?.message || 'The photo could not be prepared for upload.');
    } finally {
      setIsSanitizingPhoto(false);
    }
  };

  const preparePlansPdf = (file) => {
    if (!file) return;
    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '');
    if (!isPdf) {
      setMessage('Plans must be supplied as a PDF file.');
      return;
    }

    setMessage('');
    setDraft((current) => ({
      ...current,
      plansPdfName: file.name,
      plansPdfUploadFile: file,
      removePlansPdf: false,
    }));
  };

  const displayedPlansPdf = plansPdfPreviewUrl || (draft?.removePlansPdf ? '' : currentHome?.plansPdf || '');

  return (
    <div className="stock-admin-overlay built-review-overlay" onClick={onClose}>
      <div
        className="built-review-panel"
        onClick={(event) => event.stopPropagation()}
        onTouchStart={(event) => { touchStartX.current = event.touches[0]?.clientX ?? null; }}
        onTouchEnd={(event) => {
          if (!isReviewMode) return;
          if (touchStartX.current === null) return;
          const delta = (event.changedTouches[0]?.clientX ?? touchStartX.current) - touchStartX.current;
          if (Math.abs(delta) > 70) move(delta > 0 ? -1 : 1);
          touchStartX.current = null;
        }}
      >
        <header className="built-review-header">
          <div>
            <div className="stock-admin-title">{isReviewMode ? 'Review Built Homes' : 'Edit Home'}</div>
            {isReviewMode && <div className="built-review-progress">{queue.length ? `${currentIndex + 1} of ${queue.length} needing review` : 'Review complete'}</div>}
          </div>
          <button type="button" className="stock-admin-close-button built-admin-close-button" onClick={onClose} aria-label={isReviewMode ? 'Close review' : 'Close editor'}>×</button>
        </header>

        {!currentHome ? (
          <div className="built-review-complete">{isReviewMode ? 'There are no built homes waiting for review.' : 'This built home is no longer available.'}</div>
        ) : !isDraftReady ? (
          <div className="built-review-complete">Loading home…</div>
        ) : (
          <div className="built-review-body">
            <section className="built-review-evidence">
              {displayedPlansPdf ? (
                <>
                  <div className="built-review-evidence-toolbar">
                    {draft.plansPdfUploadFile && <span className="built-review-pdf-pending-name" title={draft.plansPdfName}>{draft.plansPdfName}</span>}
                    <a className="built-review-pdf-link" href={displayedPlansPdf} target="_blank" rel="noreferrer">Open PDF</a>
                    <button
                      type="button"
                      className="stock-admin-icon-button stock-admin-trash-button"
                      onClick={() => setDraft((current) => ({
                        ...current,
                        plansPdfName: '',
                        plansPdfUploadFile: null,
                        removePlansPdf: true,
                      }))}
                      aria-label="Delete plans PDF"
                      title="Delete plans PDF"
                    >
                      <img src={TrashIcon} alt="" className="stock-admin-trash-icon" />
                    </button>
                  </div>
                  <div className="built-review-pdf-stage">
                    <iframe src={`${displayedPlansPdf}#view=FitH&pagemode=none&navpanes=0`} title={`Consent plans for ${currentHome.clientName || currentHome.address || 'built home'}`} />
                  </div>
                </>
              ) : (
                <BuiltFileDropzone
                  accept=".pdf,application/pdf"
                  ariaLabel="Upload plans PDF"
                  className="built-review-pdf-stage built-review-pdf-dropzone"
                  dragActiveClassName="built-review-pdf-dropzone-active"
                  onFile={preparePlansPdf}
                >
                  {({ isDragActive }) => (
                    <>
                      <UploadIcon />
                      <strong>{isDragActive ? 'Drop PDF here' : 'Upload plans PDF'}</strong>
                      <span>Click to choose a file, or drag and drop a PDF here.</span>
                    </>
                  )}
                </BuiltFileDropzone>
              )}
            </section>

            <section className="built-review-details">
              <div className="built-review-fields">
                <label className="stock-admin-field stock-admin-field-full"><span>Address</span><input value={draft.address} onChange={(event) => updateDraft('address', event.target.value)} /></label>
                <div className="stock-admin-field stock-admin-field-full"><span>Coordinates</span><div className="built-coordinate-input-row"><input value={draft.coordinates} onChange={(event) => setDraft((current) => ({ ...current, coordinates: event.target.value, coordinatesEdited: true }))} placeholder="-43.5321,172.6362" /><button type="button" className="built-coordinate-map-button" onClick={() => setIsCoordinatePickerOpen(true)} aria-label="Choose coordinates on map" title="Choose coordinates on map"><MapPinIcon /></button></div></div>
                <label className="stock-admin-field stock-admin-field-full"><span>Client Name</span><input value={draft.clientName} onChange={(event) => updateDraft('clientName', event.target.value)} /></label>
                <div className="stock-admin-field"><span>Build Style</span><BuiltAdminDropdown ariaLabel="Build Style" value={draft.buildStyle} onChange={(value) => updateDraft('buildStyle', value)} placeholder="Not established" options={[{ value: '', label: 'Not established' }, ...BUILD_STYLES.map((style) => ({ value: style, label: style }))]} /></div>
                <div className="stock-admin-field"><span>Type</span><BuiltAdminDropdown ariaLabel="Type" value={draft.type} onChange={(value) => updateDraft('type', value)} placeholder="Not established" options={[{ value: '', label: 'Not established' }, ...BUILT_HOME_TYPES.map((type) => ({ value: type, label: type }))]} /></div>
                <label className="stock-admin-field"><span>Council Consented Date</span><input type="date" value={draft.consentedDate} onChange={(event) => updateDraft('consentedDate', event.target.value)} /></label>
                <label className="stock-admin-field"><span>Year Built</span><input type="number" value={draft.completedYear} onChange={(event) => updateDraft('completedYear', event.target.value)} /></label>
                <div className="stock-admin-field stock-admin-field-full"><span>Base Plan</span><BuiltAdminDropdown ariaLabel="Base Plan" value={getBasePlanSelectionValue(draft)} onChange={(value) => setDraft((current) => ({ ...current, ...getBasePlanDraftUpdate(value) }))} disabled={isLoadingBasePlans} disabledLabel="Loading plans…" placeholder="Not assigned" options={getBasePlanOptions(basePlans)} searchable searchPlaceholder="Search plans…" persistentOptionValues={[CUSTOM_BASE_PLAN_VALUE, '']} />{basePlanLoadError && <span className="stock-admin-field-error">{basePlanLoadError}</span>}</div>
                <div className="stock-admin-field stock-admin-field-full"><span>Sales Consultant</span><BuiltAdminDropdown ariaLabel="Sales Consultant" value={draft.isManualSalesConsultant ? MANUAL_CONSULTANT_VALUE : draft.salesConsultantId} onChange={(value) => {
                  setDraft((current) => ({ ...current, salesConsultantId: value === MANUAL_CONSULTANT_VALUE ? '' : value, salesConsultantManualName: value === MANUAL_CONSULTANT_VALUE ? current.salesConsultantManualName : '', isManualSalesConsultant: value === MANUAL_CONSULTANT_VALUE }));
                }} placeholder="Not assigned" options={[{ value: '', label: 'Not assigned' }, ...salesConsultants.map((consultant) => ({ value: consultant.id, label: consultant.name })), { value: MANUAL_CONSULTANT_VALUE, label: 'Enter name manually…' }]} />{draft.isManualSalesConsultant && <input className="built-manual-consultant-input" value={draft.salesConsultantManualName} onChange={(event) => updateDraft('salesConsultantManualName', event.target.value)} placeholder="Sales consultant name" autoFocus />}</div>
                <div className="stock-admin-field stock-admin-field-full">
                  <span>Photo</span>
                  {draft.photoName ? (
                    <div className="stock-admin-file-control">
                      <span className="stock-admin-file-name" title={draft.photoName}>{draft.photoName}</span>
                      <div className="stock-admin-file-actions">
                        <button type="button" className="stock-admin-icon-button stock-admin-trash-button" onClick={() => setDraft((current) => ({ ...current, photoName: '', photoUploadFile: null, removePhoto: true }))} aria-label="Remove photo" title="Remove photo">
                          <img src={TrashIcon} alt="" className="stock-admin-trash-icon" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <BuiltFileDropzone
                      accept="image/*"
                      ariaLabel="Upload photo"
                      className="stock-admin-file-control stock-admin-file-control-empty"
                      dragActiveClassName="stock-admin-file-control-drag-active"
                      disabled={isSanitizingPhoto}
                      onFile={preparePhotoFile}
                    >
                      {({ isDragActive }) => (
                        <>
                          <span className="stock-admin-file-upload-label">{isSanitizingPhoto ? 'Preparing…' : isDragActive ? 'Drop photo here' : 'Upload'}</span>
                          <div className="stock-admin-file-actions">
                            <span className="stock-admin-icon-button" aria-hidden="true">
                              <UploadIcon />
                            </span>
                          </div>
                        </>
                      )}
                    </BuiltFileDropzone>
                  )}
                </div>
                <label className="stock-admin-checkbox-row built-review-reviewed-field">
                  <input type="checkbox" checked={draft.reviewed} onChange={(event) => updateDraft('reviewed', event.target.checked)} />
                  <span>Reviewed</span>
                </label>
              </div>

              <div className="built-review-provenance">
                <div><strong>Source plan</strong><span>{currentHome.sourcePlanPath || 'Not established'}</span></div>
                {currentHome.importWarnings?.length > 0 && <div className="built-review-warnings"><strong>Import warnings</strong><ul>{currentHome.importWarnings.map((warning) => <li key={warning}>{warning}</li>)}</ul></div>}
              </div>

              <div className="built-review-message" role="status">{message}</div>
              <div className="built-review-actions">
                {isReviewMode && <button type="button" className="stock-admin-secondary-button built-review-arrow-button" onClick={() => move(-1)} disabled={currentIndex === 0 || isSaving || isSanitizingPhoto} aria-label="Previous home" title="Previous home"><ReviewChevron direction="left" /></button>}
                {isReviewMode && <button type="button" className="stock-admin-secondary-button built-review-arrow-button" onClick={() => move(1)} disabled={currentIndex >= queue.length - 1 || isSaving || isSanitizingPhoto} aria-label="Next home" title="Next home"><ReviewChevron direction="right" /></button>}
                <button type="button" className="stock-admin-secondary-button" onClick={() => saveCurrent()} disabled={isSaving || isSanitizingPhoto}>{isSaving ? 'Saving…' : isSanitizingPhoto ? 'Preparing Photo…' : 'Save'}</button>
                <button type="button" className="stock-admin-primary-button" onClick={() => saveCurrent({ closeAfterSave: true })} disabled={isSaving || isSanitizingPhoto}>{isSaving ? 'Saving…' : isSanitizingPhoto ? 'Preparing Photo…' : 'Save & Close'}</button>
              </div>
            </section>
          </div>
        )}
      </div>
      <BuiltCoordinatePicker
        isOpen={isCoordinatePickerOpen}
        address={draft?.address || ''}
        initialCoordinates={(() => {
          try { return parseCoordinates(draft?.coordinates) || currentHome?.coordinates || null; }
          catch (error) { return currentHome?.coordinates || null; }
        })()}
        onCancel={() => setIsCoordinatePickerOpen(false)}
        onApply={(position) => {
          setDraft((current) => ({ ...current, coordinates: formatCoordinates(position.lat, position.lng), coordinatesEdited: true }));
          setIsCoordinatePickerOpen(false);
        }}
      />
    </div>
  );
}
